import type { FC } from 'react'
import React, { useState, useCallback } from 'react'
import type { Theme } from '@material-ui/core/styles'
import { makeStyles, createStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import { useDropzone } from 'react-dropzone'

import { Typography, Button } from '@homeviews/shared-ui'
import type { UploadFile } from '@homeviews/models'
import { mixpanelTrack } from '@homeviews/seo'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: { position: 'relative' },
    title: {
      position: 'absolute',
      top: 6,
      left: 0,
    },
    uploadButtonWrapper: {
      maxWidth: 113,
      marginLeft: 75,
    },
    zoneWrapper: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: 300,
    },
    zoneInner: {},
    zoneContent: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    },
    selectFilesLabel: {
      marginTop: 10,
      marginBottom: 10,
    },
    selectFilesButton: {
      marginTop: 10,
      width: 149,
    },
    allowedTypesText: {
      marginTop: 10,
      fontSize: 12,
      color: theme.colors.grey450,
    },
    infoText: {
      marginTop: 10,
      fontSize: 12,
      color: theme.colors.grey450,
      textAlign: 'center',
      maxWidth: 400,
    },
    uploadedFileInfo: {
      marginTop: 5,
    },
  }),
)

interface IProps {
  addMedia: any
  isAddingMedia?: boolean
  error: any
}

const UploadZone: FC<IProps> = ({ addMedia, isAddingMedia, error }) => {
  const classes = useStyles()
  const [zoneOpened, setZoneOpened] = useState<boolean>(false)
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([])

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0]
    const fileExtensionRegex = /\.[^/.]+$/
    const fileName = acceptedFiles[0]?.name.replace(fileExtensionRegex, '')
    const payload: UploadFile = {
      file,
      title: fileName,
      collection_type: 'default',
      alt_text: fileName,
    }

    setUploadedFiles([acceptedFiles[0]])

    addMedia(payload)
  }, [])

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
  })

  return (
    <div className={classes.root}>
      <Typography variant="h3" className={classes.title}>
        Upload
      </Typography>
      {!zoneOpened ? (
        <div className={classes.uploadButtonWrapper}>
          <Button
            variant="outlined"
            customVariant="outlinedPrimary"
            fullWidth
            onClick={() => {
              mixpanelTrack('MEDIA_ADD_NEW_MEDIA')
              setZoneOpened(true)
            }}
          >
            Add new media
          </Button>
        </div>
      ) : (
        <div className={classes.zoneWrapper} {...getRootProps()}>
          <div className={classes.zoneInner}>
            <div className={classes.zoneContent}>
              <input {...getInputProps()} />

              <Typography className={classes.selectFilesLabel}>
                {isDragActive
                  ? 'Drop the files here ...'
                  : 'Select files or drag and drop to upload'}
              </Typography>
              <img
                src={`assets/icons/huge/image-add.svg`}
                alt={`Logout icon`}
              />
              <Button
                variant="outlined"
                customVariant="outlinedPrimary"
                fullWidth
                onClick={open}
                size="small"
                className={classes.selectFilesButton}
                isLoading={isAddingMedia}
              >
                Select files for upload
              </Button>
              {!!uploadedFiles?.length &&
                uploadedFiles.map((file: any, index) => (
                  <Typography key={index} className={classes.uploadedFileInfo}>
                    {index > 0 && ', '}
                    {file?.name || 'unknown'}
                  </Typography>
                ))}
              <Typography className={classes.allowedTypesText}>
                PNG, JPG, GIF, SVG up to 10Mb
              </Typography>
              <Typography className={classes.infoText}>
                When naming your images please include the name of the
                development where possible and a brief indication of the
                content.
              </Typography>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default UploadZone
